import { Box, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type {
  ReviewReasonEvidence,
  ReviewReasonEvidenceTone,
} from '../../lib/employerReview/reviewReason.types';

function toneStyles(tone: ReviewReasonEvidenceTone | undefined, isLight: boolean) {
  switch (tone) {
    case 'declared':
      return {
        bgcolor: isLight ? alpha('#F9AB00', 0.14) : alpha('#FDD663', 0.16),
        borderColor: isLight ? alpha('#F9AB00', 0.45) : alpha('#FDD663', 0.4),
        labelColor: isLight ? '#9A6700' : '#FDD663',
      };
    case 'observed':
      return {
        bgcolor: isLight ? alpha('#7C3AED', 0.1) : alpha('#C4B5FD', 0.14),
        borderColor: isLight ? alpha('#7C3AED', 0.35) : alpha('#C4B5FD', 0.35),
        labelColor: isLight ? '#6D28D9' : '#DDD6FE',
      };
    case 'employer':
      return {
        bgcolor: isLight ? alpha('#1D4ED8', 0.08) : alpha('#93C5FD', 0.14),
        borderColor: isLight ? alpha('#1D4ED8', 0.35) : alpha('#93C5FD', 0.35),
        labelColor: isLight ? '#1E40AF' : '#BFDBFE',
      };
    case 'role':
      return {
        bgcolor: isLight ? alpha('#0E7490', 0.1) : alpha('#5EEAD4', 0.12),
        borderColor: isLight ? alpha('#0E7490', 0.35) : alpha('#5EEAD4', 0.35),
        labelColor: isLight ? '#0F766E' : '#99F6E4',
      };
    case 'workplace':
      return {
        bgcolor: isLight ? alpha('#BE185D', 0.08) : alpha('#F9A8D4', 0.12),
        borderColor: isLight ? alpha('#BE185D', 0.32) : alpha('#F9A8D4', 0.32),
        labelColor: isLight ? '#9D174D' : '#FBCFE8',
      };
    case 'policy':
      return {
        bgcolor: isLight ? alpha('#4338CA', 0.08) : alpha('#A5B4FC', 0.14),
        borderColor: isLight ? alpha('#4338CA', 0.32) : alpha('#A5B4FC', 0.32),
        labelColor: isLight ? '#3730A3' : '#C7D2FE',
      };
    case 'places':
      return {
        bgcolor: isLight ? alpha('#047857', 0.09) : alpha('#6EE7B7', 0.12),
        borderColor: isLight ? alpha('#047857', 0.3) : alpha('#6EE7B7', 0.28),
        labelColor: isLight ? '#065F46' : '#A7F3D0',
      };
    case 'reviews':
      return {
        bgcolor: isLight ? alpha('#B45309', 0.1) : alpha('#FCD34D', 0.12),
        borderColor: isLight ? alpha('#B45309', 0.32) : alpha('#FCD34D', 0.28),
        labelColor: isLight ? '#92400E' : '#FDE68A',
      };
    case 'vision':
      return {
        bgcolor: isLight ? alpha('#E11D48', 0.07) : alpha('#FDA4AF', 0.12),
        borderColor: isLight ? alpha('#E11D48', 0.28) : alpha('#FDA4AF', 0.28),
        labelColor: isLight ? '#BE123C' : '#FECDD3',
      };
    case 'satellite':
      return {
        bgcolor: isLight ? alpha('#0369A1', 0.08) : alpha('#7DD3FC', 0.12),
        borderColor: isLight ? alpha('#0369A1', 0.3) : alpha('#7DD3FC', 0.28),
        labelColor: isLight ? '#075985' : '#BAE6FD',
      };
    case 'domain':
      return {
        bgcolor: isLight ? alpha('#334155', 0.08) : alpha('#94A3B8', 0.14),
        borderColor: isLight ? alpha('#334155', 0.28) : alpha('#94A3B8', 0.28),
        labelColor: isLight ? '#1E293B' : '#CBD5E1',
      };
    case 'registry':
      return {
        bgcolor: isLight ? alpha('#991B1B', 0.07) : alpha('#FCA5A5', 0.12),
        borderColor: isLight ? alpha('#991B1B', 0.28) : alpha('#FCA5A5', 0.28),
        labelColor: isLight ? '#7F1D1D' : '#FECACA',
      };
    case 'aiInsight':
      return {
        bgcolor: isLight ? alpha('#6D28D9', 0.07) : alpha('#C4B5FD', 0.12),
        borderColor: isLight ? alpha('#6D28D9', 0.28) : alpha('#C4B5FD', 0.28),
        labelColor: isLight ? '#5B21B6' : '#DDD6FE',
      };
    case 'threshold':
      return {
        bgcolor: isLight ? alpha('#78716C', 0.08) : alpha('#D6D3D1', 0.12),
        borderColor: isLight ? alpha('#78716C', 0.28) : alpha('#D6D3D1', 0.28),
        labelColor: isLight ? '#57534E' : '#E7E5E4',
      };
    default:
      return {
        bgcolor: isLight ? alpha('#5F6368', 0.08) : alpha('#9AA0A6', 0.12),
        borderColor: isLight ? alpha('#5F6368', 0.25) : alpha('#9AA0A6', 0.3),
        labelColor: isLight ? '#5F6368' : '#9AA0A6',
      };
  }
}

function DataPointChip({ label, value, tone }: ReviewReasonEvidence) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const styles = toneStyles(tone, isLight);

  return (
    <Box
      sx={{
        px: 1,
        py: 0.65,
        borderRadius: 1.25,
        border: '1px solid',
        bgcolor: styles.bgcolor,
        borderColor: styles.borderColor,
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontWeight: 800,
          letterSpacing: 0.35,
          textTransform: 'uppercase',
          fontSize: '0.63rem',
          color: styles.labelColor,
          mb: 0.15,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35, fontSize: '0.8125rem' }}>
        {value}
      </Typography>
    </Box>
  );
}

/** 3–4 scannable data points + optional issue line for credit adjudicators. */
export function AdjudicatorDataPointsPanel({
  dataPoints,
  secondaryDataPoints,
  secondarySectionLabel = 'Collected',
  issueStatement,
  issueSeverity = 'warning',
  issueLabel = 'Issue',
  footerNote,
  advisoryLabel,
  advisorySeverity = 'warning',
}: {
  dataPoints: ReviewReasonEvidence[];
  secondaryDataPoints?: ReviewReasonEvidence[];
  secondarySectionLabel?: string;
  issueStatement?: string | null;
  issueSeverity?: 'warning' | 'error' | 'info';
  issueLabel?: string;
  footerNote?: string | null;
  advisoryLabel?: string | null;
  advisorySeverity?: 'warning' | 'success' | 'default';
}) {
  const theme = useTheme();
  if (!dataPoints.length && !issueStatement) return null;

  const advisoryColor =
    advisorySeverity === 'success'
      ? 'success'
      : advisorySeverity === 'warning'
        ? 'warning'
        : 'default';

  return (
    <Stack spacing={0.85}>
      {advisoryLabel ? (
        <Box
          sx={{
            alignSelf: 'flex-start',
            px: 1,
            py: 0.35,
            borderRadius: 999,
            bgcolor: alpha(
              theme.palette[advisoryColor === 'default' ? 'warning' : advisoryColor].main,
              theme.palette.mode === 'light' ? 0.12 : 0.18,
            ),
            border: '1px solid',
            borderColor: alpha(
              theme.palette[advisoryColor === 'default' ? 'warning' : advisoryColor].main,
              0.35,
            ),
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              letterSpacing: 0.35,
              textTransform: 'uppercase',
              color: `${advisoryColor === 'default' ? 'warning' : advisoryColor}.dark`,
            }}
          >
            {advisoryLabel}
          </Typography>
        </Box>
      ) : null}

      {dataPoints.length ? (
        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {dataPoints.map((row) => (
            <Box key={`${row.label}-${row.value}`} sx={{ flex: '1 1 140px', minWidth: 0 }}>
              <DataPointChip {...row} />
            </Box>
          ))}
        </Stack>
      ) : null}

      {secondaryDataPoints?.length ? (
        <Stack spacing={0.45}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              letterSpacing: 0.35,
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            {secondarySectionLabel}
          </Typography>
          <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {secondaryDataPoints.map((row) => (
              <Box key={`${row.label}-${row.value}`} sx={{ flex: '1 1 140px', minWidth: 0 }}>
                <DataPointChip {...row} />
              </Box>
            ))}
          </Stack>
        </Stack>
      ) : null}

      {issueStatement ? (
        <Box
          sx={{
            px: 1,
            py: 0.7,
            borderRadius: 1.25,
            borderLeft: '3px solid',
            borderColor: `${issueSeverity}.main`,
            bgcolor: alpha(
              theme.palette[issueSeverity].main,
              theme.palette.mode === 'light' ? 0.08 : 0.14,
            ),
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 800,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              color: `${issueSeverity}.main`,
              mb: 0.25,
            }}
          >
            {issueLabel}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.45, fontSize: '0.8125rem' }}>
            {issueStatement}
          </Typography>
        </Box>
      ) : null}

      {footerNote ? (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
          {footerNote}
        </Typography>
      ) : null}
    </Stack>
  );
}
